// based on https://www.radix-ui.com/primitives/docs/components/accordion#api-reference

import React, { JSX } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import styles from './accordion.module.scss';
import { Plus, Minus, ChevronDown, ChevronDownFAQ } from '../../icons';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={`${styles.accordionItem} ${className}`}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    icon?: 'plusMinus' | 'chevron' | 'chevronFAQ';
  }
>(({ className, children, icon = 'plusMinus', ...props }, ref) => (
  <AccordionPrimitive.Header className={styles.accordionHeader}>
    <AccordionPrimitive.Trigger
      ref={ref}
      className={`${styles.accordionTrigger} ${className}`}
      {...props}
    >
      <div className={styles.accordionTriggerChildren}>{children}</div>
      {icon === 'plusMinus' ? (
        <>
          <Plus
            className={`${styles.accordionIcon} ${styles.accordionIconClosed}`}
          />
          <Minus
            className={`${styles.accordionIcon} ${styles.accordionIconOpen}`}
          />
        </>
      ) : icon === 'chevronFAQ' ? (
        <ChevronDownFAQ
          className={`${styles.accordionIcon} ${styles.accordionIconChevron}`}
        />
      ) : (
        <ChevronDown
          className={`${styles.accordionIcon} ${styles.accordionIconChevron}`}
        />
      )}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={`${styles.accordionContent} ${className}`}
    {...props}
  >
    {children}
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };

type AccordionItem = {
  title: string;
  content: string | JSX.Element;
  value: string;
};

export default function BaseAccordion<T>({
  items,
  ...props
}: Readonly<
  { items: (T & AccordionItem)[] } & React.ComponentProps<typeof Accordion>
>) {
  return (
    <Accordion {...props}>
      {items.map((item) => (
        <AccordionItem key={item.value} value={item.value}>
          <AccordionTrigger>{item.title}</AccordionTrigger>
          <AccordionContent>
            {item.content}
            <div className={styles.contentMobileSpacer} />
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
